import {createContext} from 'react';
import type {CSSProperties, ReactNode} from 'react';

// Configuration options for a popout window
export interface PopoutWindowFeatures {
  width?: number;
  height?: number;
  left?: number;
  top?: number;
}
// ---------------

// Options for opening a popout
export interface PopoutOptions {
  title?: string;
  windowFeatures?: PopoutWindowFeatures;
  copyStyles?: boolean;
  observeStyles?: boolean;
}
// ---------------

//  * Represents a single popout window instance
export interface PopoutInstance {
  id: string;
  windowRef: Window;
  containerRef: HTMLElement;
  title: string;
  isReady: boolean;
  options: PopoutOptions;
}
// ---------------

export interface PortalPopoutManagerContextValue {
  popouts: Map<string, PopoutInstance>;
  openPopout: (id: string, options?: PopoutOptions) => void;
  closePopout: (id: string) => void;
  closeAll: () => void;
  getPopout: (id: string) => PopoutInstance | undefined;
  focusPopout: (id: string) => void;
  isPopoutOpen: (id: string) => boolean;
  registerContent: (id: string, content: ReactNode) => void;
  unregisterContent: (id: string) => void;
  getContent: (id: string) => ReactNode | undefined;
}
// ---------------

// Props for PortalPopoutContent component
export interface PortalPopoutContentProps {
  popoutId: string;
  children: ReactNode;
  placeholder?: ReactNode;
  className?: string;
  style?: CSSProperties;
  title?: string;
  windowFeatures?: PopoutWindowFeatures;
  tabRoot?: string;
}
// ---------------

// Render prop arguments for PortalPopoutTrigger
export interface PopoutTriggerRenderProps {
  isOpen: boolean;
  isReady: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  focus: () => void;
}
// ---------------

// Props for PortalPopoutTrigger component
export interface PortalPopoutTriggerProps {
  popoutId: string;
  children: (props: PopoutTriggerRenderProps) => ReactNode;
}
// ---------------

// Props for PortalPopoutManager component
export interface PortalPopoutManagerProps {
  children: ReactNode;
  defaultOptions?: PopoutOptions;
  onPopoutOpen?: (id: string) => void;
  onPopoutClose?: (id: string) => void;
}

//  Default window features for popouts
export const DEFAULT_WINDOW_FEATURES: PopoutWindowFeatures = {
  width: 600,
  height: 400,
  left: 100,
  top: 100,
};
// ---------------

// Default popout options
export const DEFAULT_POPOUT_OPTIONS: PopoutOptions = {
  title: 'Popout Window',
  windowFeatures: DEFAULT_WINDOW_FEATURES,
  copyStyles: true,
  observeStyles: true,
};
// ---------------

// Context for PortalPopoutManager
export const PortalPopoutManagerContext =
  createContext<PortalPopoutManagerContextValue | null>(null);
// ---------------

// Build window features string from options
export const buildWindowFeatures = (features: PopoutWindowFeatures): string => {
  const merged = {...DEFAULT_WINDOW_FEATURES, ...features};
  return Object.entries(merged)
    .map(([key, value]) => `${key}=${value}`)
    .join(',');
};
